export interface TimeRemaining {
  months: number;
  days: number;
  hours: number;
  minutes: number;
  isPast: boolean;
}

/**
 * Helper: Adds months to a date while handling end-of-month overflow.
 * Example: Adding 1 month to Jan 31st becomes Feb 28th (or 29th), not March 3rd.
 */
function addMonths(date: Date, monthsToAdd: number): Date {
  const newDate = new Date(date);
  const currentDay = newDate.getDate();

  // Set the first day of the month to avoid overflow errors immediately
  newDate.setDate(1);
  // Add the months
  newDate.setMonth(newDate.getMonth() + monthsToAdd);

  // Check how many days are in this new specific month
  // (We go to the next month's 0th day, which is the previous month's last day)
  const daysInNewMonth = new Date(
    newDate.getFullYear(),
    newDate.getMonth() + 1,
    0
  ).getDate();

  // Restore the original day, capped at the new month's length
  newDate.setDate(Math.min(currentDay, daysInNewMonth));

  return newDate;
}

export function getTimeUntil(targetDate: Date | string): TimeRemaining {
  // Normalize input to Date object
  const target =
    typeof targetDate === "string" ? new Date(targetDate) : targetDate;
  const now = new Date();

  // Initialize result
  const result: TimeRemaining = {
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    isPast: false,
  };

  if (target <= now) {
    result.isPast = true;
    return result;
  }

  // --- 1. Calculate Months ---
  // We iteratively add months to "now" until we get as close to the target as possible
  // without passing it.
  let tempDate = new Date(now);
  let monthsCount = 0;

  while (true) {
    const nextMonthDate = addMonths(tempDate, 1);
    if (nextMonthDate > target) {
      break;
    }
    tempDate = nextMonthDate; // Advance our temp pointer
    monthsCount++;
  }
  result.months = monthsCount;

  // --- 2. Calculate Days, Hours, Minutes from the remainder ---
  // tempDate is now the exact same time of day as 'now', but X months in the future.
  // We calculate the difference between that tempDate and the target.
  let diffMs = target.getTime() - tempDate.getTime();

  // Constants for conversions
  const MS_PER_MINUTE = 1000 * 60;
  const MS_PER_HOUR = MS_PER_MINUTE * 60;
  const MS_PER_DAY = MS_PER_HOUR * 24;

  result.days = Math.floor(diffMs / MS_PER_DAY);
  diffMs -= result.days * MS_PER_DAY;

  result.hours = Math.floor(diffMs / MS_PER_HOUR);
  diffMs -= result.hours * MS_PER_HOUR;

  result.minutes = Math.floor(diffMs / MS_PER_MINUTE);

  return result;
}
